/* ==========================================================================
   HMS cart guard
   --------------------------------------------------------------------------
   Keeps Howell Mountain school data off non-Howell-Mountain orders.

   THE PROBLEM THIS SOLVES
   The student fields write Shopify CART ATTRIBUTES, which belong to the cart
   rather than to a line item. snippets/hms-student-fields.liquid correctly
   renders nothing unless the cart holds a product tagged 'Howell Mountain',
   so a regular shopper never SEES a school field. But the stored values
   survived removing the HMS product:

     add HMS hoodie -> type the student's name -> remove the hoodie
     -> add an ordinary tee -> check out

   left 'Student Name' and 'Homeroom Teacher' attached to an ordinary Mendo
   Midnight order. The fields were hidden; the child's name still rode along.
   Verified on the storefront 2026-09-13 before this guard existed.

   HOW IT DECIDES, AND WHY NOT FROM THE DOM
   An earlier version asked the DOM whether a .hms-fields block was present.
   That is wrong. Removing an item on /cart re-renders main-cart-items and
   main-cart-footer but NOT the cart drawer, which theme.liquid rendered at
   page load, so a stale .hms-fields from the drawer stayed in the document
   and the guard concluded the cart was still a school cart. Measured: cart
   empty, attributes still set, guard silent.

   It now asks the SERVER, through the Section Rendering API, and the server
   answers with the same Liquid tag test that gates the fields themselves.
   One identifier, no duplicated product list, nothing to keep in sync when
   HMS products are added or removed.

   The section request only happens when there is actually school data to
   protect, so an ordinary shopper costs one small /cart.js read.

   Fail-open by design: every step is wrapped so a network error or a missing
   global can never block a cart action or a checkout.

   NOTE: line-item properties would make this structurally impossible rather
   than needing a guard, because they are destroyed with their line item. That
   would move the student's name onto each product page instead of the cart.
   The cart-level model was chosen deliberately; this guard is what makes it
   safe.
   ========================================================================== */

(function () {
  var KEYS = ['Student Name', 'Homeroom Teacher'];
  var SECTION = 'main-cart-footer';
  var MARKER = 'hms-fields';
  var busy = false;

  function cartRoot() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  function cartUpdateUrl() {
    return (window.routes && window.routes.cart_update_url) || '/cart/update.js';
  }

  function hasSchoolData(cart) {
    var attrs = (cart && cart.attributes) || {};
    return KEYS.some(function (k) {
      return attrs[k] !== undefined && attrs[k] !== null && attrs[k] !== '';
    });
  }

  function clearSchoolData() {
    var payload = { attributes: {} };
    KEYS.forEach(function (k) { payload.attributes[k] = ''; });
    return fetch(cartUpdateUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
  }

  function reconcile() {
    if (busy) return;
    busy = true;

    fetch(cartRoot() + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        /* Nothing stored means nothing to leak. Costs one small read and
           short-circuits for every ordinary shopper. */
        if (!hasSchoolData(cart)) return null;

        /* Ask the server whether this cart still renders the school block.
           Authoritative: same product-tag test as the fields themselves. */
        return fetch(cartRoot() + '?section_id=' + SECTION, { credentials: 'same-origin' })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            if (html.indexOf(MARKER) !== -1) return null; /* still a school cart */
            return clearSchoolData();
          });
      })
      .catch(function () { /* never block the cart */ })
      .then(function () { busy = false; }, function () { busy = false; });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }

  /* Dawn republishes this after every cart mutation, including a remove from
     the drawer. pubsub.js declares these as top-level bindings in a classic
     script, so they are bare identifiers rather than properties of window. */
  try {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        setTimeout(reconcile, 120);
      });
    }
  } catch (e) {
    /* older or modified theme: the load-time pass still covers navigation */
  }
})();
